import React, {useState, useEffect} from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction,
    Avatar,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Chip,
} from '@mui/material';
import {
    PersonAdd as PersonAddIcon,
    Person as PersonIcon,
    Delete as DeleteIcon,
    School as SchoolIcon,
} from '@mui/icons-material';

const CourseTeachersTab = ({courseId, apiClient}) => {
    const [teachers, setTeachers] = useState([]);
    const [courseCreatorId, setCourseCreatorId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [teacherEmail, setTeacherEmail] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Fetch course details including teachers
    const fetchTeachers = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get(`/api/courses/${courseId}`);
            // Store the course creator ID and teachers array
            setCourseCreatorId(response.teacher_id || response.teacher?.id || null);
            setTeachers(response.teachers || []);
        } catch (err) {
            console.error('Error fetching course teachers:', err);
            setError('Failed to load teachers');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (courseId && apiClient) {
            fetchTeachers();
        }
    }, [courseId, apiClient]);

    const handleAddTeacher = async () => {
        if (!teacherEmail.trim()) {
            setError('Please enter a teacher email');
            return;
        }

        setAdding(true);
        setError(null);
        setSuccess(null);

        try {
            await apiClient.post(`/api/courses/${courseId}/teachers`, {
                teacher_email: teacherEmail.trim()
            });

            setSuccess('Teacher added successfully');
            setTeacherEmail('');
            setAddDialogOpen(false);
            await fetchTeachers(); // Refresh the list
        } catch (err) {
            console.error('Error adding teacher:', err);
            setError(err.response?.data?.detail || 'Failed to add teacher');
        } finally {
            setAdding(false);
        }
    };

    const handleRemoveTeacher = async (teacherId) => {
        if (!confirm('Are you sure you want to remove this teacher from the course?')) {
            return;
        }

        try {
            await apiClient.delete(`/api/courses/${courseId}/teachers/${teacherId}`);
            setSuccess('Teacher removed successfully');
            await fetchTeachers(); // Refresh the list
        } catch (err) {
            console.error('Error removing teacher:', err);
            setError(err.response?.data?.detail || 'Failed to remove teacher');
        }
    };

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
                <CircularProgress/>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Box>
                    <Typography variant="h6" sx={{display: 'flex', alignItems: 'center'}}>
                        <SchoolIcon sx={{mr: 1}}/>
                        Course Teachers ({teachers.length})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage who can edit this course
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<PersonAddIcon/>}
                    onClick={() => setAddDialogOpen(true)}
                >
                    Add Teacher
                </Button>
            </Box>

            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{mb: 2}} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Teachers List */}
            <Paper>
                <Box sx={{p: 2, borderBottom: 1, borderColor: 'divider'}}>
                    <Typography variant="h6">Teachers</Typography>
                </Box>

                {teachers.length === 0 ? (
                    <Box sx={{p: 4, textAlign: 'center'}}>
                        <SchoolIcon sx={{fontSize: 48, color: 'text.secondary', mb: 2}}/>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                            No teachers assigned yet
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<PersonAddIcon/>}
                            onClick={() => setAddDialogOpen(true)}
                            sx={{mt: 2}}
                        >
                            Add Your First Teacher
                        </Button>
                    </Box>
                ) : (
                    <List>
                        {teachers.map((teacher) => (
                            <ListItem key={teacher.id}>
                                <ListItemAvatar>
                                    <Avatar sx={{bgcolor: 'primary.main'}}>
                                        <PersonIcon/>
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                            {teacher.name || teacher.email}
                                            {teacher.id === courseCreatorId && (
                                                <Chip
                                                    label="Course Creator"
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                    }
                                    secondary={
                                        <Box>
                                            <Box component="div" sx={{fontSize: '0.875rem', color: 'text.secondary'}}>
                                                {teacher.email}
                                            </Box>
                                            <Box component="div" sx={{fontSize: '0.75rem', color: 'text.secondary'}}>
                                                Can edit course content, manage students, and create topics
                                            </Box>
                                        </Box>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    {teachers.length > 1 && ( // Don't allow removing the last teacher
                                        <IconButton
                                            edge="end"
                                            aria-label="remove teacher"
                                            onClick={() => handleRemoveTeacher(teacher.id)}
                                            color="error"
                                        >
                                            <DeleteIcon/>
                                        </IconButton>
                                    )}
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Paper>

            {/* Add Teacher Dialog */}
            <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Teacher to Course</DialogTitle>
                <DialogContent>
                    <Box sx={{mt: 1}}>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                            Enter the email address of the teacher you want to add. They must already have a teacher account.
                        </Typography>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Teacher Email"
                            type="email"
                            value={teacherEmail}
                            onChange={(e) => setTeacherEmail(e.target.value)}
                            placeholder="teacher@example.com"
                            disabled={adding}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialogOpen(false)} disabled={adding}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddTeacher}
                        variant="contained"
                        disabled={adding || !teacherEmail.trim()}
                        startIcon={adding ? <CircularProgress size={20}/> : <PersonAddIcon/>}
                    >
                        {adding ? 'Adding...' : 'Add Teacher'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CourseTeachersTab;